/**
 * The page Teslyn Mobile's sign-in email links to (`/mobile/signed-in`; docs/specs/cloud/sign-in-security.md §3.1).
 * The phone app asks for its email link with its own return address (`au.teslyn.mobile.beta://signed-in`), which a
 * mail app cannot open as a link, so the Send Email hook links to this page on the project's Site URL with the token
 * hash and `?to=<the app's address>` (packages/domain auth-email.ts `authLinkUrl`). The page spends nothing on load:
 * "Open Teslyn Mobile" opens Supabase's own link (`/auth/v1/verify?token=…&redirect_to=<app>`), which spends the
 * token and sends the phone's browser to the app with the `?code=` the app exchanges with its code verifier, as
 * before. `to` is accepted only when it is one of the app's two addresses (`mobileReturnAddress`), never an address
 * the link chose. This address needs no tenant and no session.
 */
import { useState, type ReactElement } from 'react';
import type { EmailOtpType } from '@supabase/supabase-js';
import {
  MOBILE_RETURN_PATH,
  MOBILE_RETURN_TO_PARAM,
  authVerifyUrl,
  mobileReturnAddress,
  type AuthEmailAction,
} from '@teslyn/domain';
import { Button } from '../components/Button.tsx';
import { PoweredBy } from '../components/States.tsx';
import { readConfig } from '../config.ts';
import { LegalLinks } from '../legal/LegalPage.tsx';

/** Whether this address is Teslyn Mobile's return page. */
export function isMobileReturnPage(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === MOBILE_RETURN_PATH;
}

const LINK_TYPES: readonly string[] = ['invite', 'magiclink', 'email', 'signup', 'recovery', 'email_change'];

export type MobileReturn =
  | { readonly kind: 'link'; readonly tokenHash: string; readonly type: EmailOtpType; readonly to: string }
  | { readonly kind: 'invalid'; readonly why: string };

/** What the address carries: the link's parts, or why the page cannot open the app. */
export function readMobileReturn(search: string): MobileReturn {
  const q = new URLSearchParams(search);
  const to = mobileReturnAddress(q.get(MOBILE_RETURN_TO_PARAM));
  const tokenHash = q.get('token_hash') ?? '';
  const type = q.get('type') ?? '';
  if (!to) return { kind: 'invalid', why: 'This link does not name Teslyn Mobile.' };
  if (!tokenHash || !LINK_TYPES.includes(type))
    return { kind: 'invalid', why: 'This link is incomplete. Ask for a new one in Teslyn Mobile.' };
  return { kind: 'link', tokenHash, type: type as EmailOtpType, to };
}

/** Supabase's link for the app: it spends the token and sends the browser to the app with the code. */
export function mobileOpenLink(supabaseUrl: string, r: Extract<MobileReturn, { kind: 'link' }>): string {
  return authVerifyUrl(supabaseUrl, r.tokenHash, r.type as AuthEmailAction, r.to);
}

export function MobileSignedIn({
  location = window.location,
  open = (url) => window.location.replace(url),
  supabaseUrl = readConfig().supabaseUrl,
}: {
  readonly location?: { readonly search: string };
  /** Opens Supabase's link (the browser then asks to open Teslyn Mobile). */
  readonly open?: (url: string) => void;
  readonly supabaseUrl?: string;
}): ReactElement {
  const r = readMobileReturn(location.search);
  const [opened, setOpened] = useState(false);
  return (
    <div className="signin">
      <main className="signin__card" aria-labelledby="mobile-return-title">
        <h1 id="mobile-return-title" className="title">
          {r.kind === 'link' ? 'Sign in to Teslyn Mobile' : 'This link cannot open Teslyn Mobile'}
        </h1>
        {r.kind === 'link' ? (
          <>
            <p className="body">
              You opened a sign-in link from a Teslyn email. Press the button on the phone that asked for it,
              and allow the browser to open Teslyn Mobile. Nothing happens until you press it; if you did not
              ask for this email, close this page and ignore the email.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setOpened(true);
                open(mobileOpenLink(supabaseUrl, r));
              }}
            >
              Open Teslyn Mobile
            </Button>
            {opened && (
              <p className="caption muted" role="status">
                Teslyn Mobile is finishing your sign-in. You can close this window.
              </p>
            )}
          </>
        ) : (
          <p className="body" role="alert">
            {r.why}
          </p>
        )}
      </main>
      <footer className="signin__foot">
        <PoweredBy />
        <LegalLinks />
      </footer>
    </div>
  );
}
